import React, { useEffect, useState } from "react";
import { Plus, Copy, Check, Power, Pause } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { useT } from "../i18n";

const BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || "Clipra_bot";

function fmtUsd(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

export default function AffCodes() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/affiliate/codes");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-5" data-testid="aff-codes-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">{t("codes.title")}</h1>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">
            {t("codes.subtitle")}
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="aff-create-code-btn"
          className="px-3 py-1.5 text-sm bg-foreground text-background flex items-center gap-1.5 hover:opacity-90"
        >
          <Plus className="w-3.5 h-3.5" />
          {t("codes.create")}
        </button>
      </header>

      <div className="border border-border" data-testid="aff-codes-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/30 text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              <th className="text-left px-3 py-2">{t("codes.col.code")}</th>
              <th className="text-left px-3 py-2">{t("codes.col.label")}</th>
              <th className="text-right px-3 py-2">{t("codes.col.signups")}</th>
              <th className="text-right px-3 py-2">{t("codes.col.earned")}</th>
              <th className="text-left px-3 py-2">{t("codes.col.status")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="6" className="px-3 py-6 text-center text-muted-foreground text-xs">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="6" className="px-3 py-8 text-center text-muted-foreground text-xs">
                  {t("codes.empty")}
                </td>
              </tr>
            )}
            {items.map((c) => (
              <CodeRow key={c.id} code={c} onChanged={load} t={t} />
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateCodeModal
          t={t}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function CodeRow({ code, onChanged, t }) {
  const [showLinks, setShowLinks] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const tgLink = `https://t.me/${BOT_USERNAME}?start=aff_${code.code}`;
  const webLink = `${window.location.origin}/?ref=${code.code}`;

  const copy = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const toggle = async () => {
    const next = code.status === "disabled" ? "active" : "disabled";
    try {
      await api.patch(`/affiliate/codes/${code.id}`, { status: next });
      toast.success(next === "active" ? t("codes.activated") : t("codes.disabled"));
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <tr
        className="border-b border-border hover:bg-secondary/20"
        data-testid={`aff-code-row-${code.code}`}
      >
        <td className="px-3 py-2.5 font-mono text-emerald-400">{code.code}</td>
        <td className="px-3 py-2.5 text-muted-foreground">{code.label || t("common.dash")}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{code.registrations ?? 0}</td>
        <td className="px-3 py-2.5 text-right tabular-nums">{fmtUsd(code.earned_usd)}</td>
        <td className="px-3 py-2.5">
          <span
            className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border ${
              code.status === "active"
                ? "border-emerald-500/40 text-emerald-400"
                : "border-border text-muted-foreground"
            }`}
          >
            {code.status}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right">
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowLinks((v) => !v)}
              data-testid={`aff-toggle-links-${code.code}`}
              className="text-xs text-muted-foreground hover:text-foreground px-2"
            >
              {showLinks ? t("codes.row.hide") : t("codes.row.links")}
            </button>
            <button
              onClick={toggle}
              data-testid={`aff-toggle-status-${code.code}`}
              title={code.status === "disabled" ? t("codes.row.enable") : t("codes.row.disable")}
              className="text-muted-foreground hover:text-foreground px-2"
            >
              {code.status === "disabled" ? (
                <Power className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </td>
      </tr>
      {showLinks && (
        <tr className="border-b border-border bg-secondary/10">
          <td colSpan="6" className="px-3 py-3 space-y-2">
            <LinkRow
              label={t("codes.link.telegram")}
              url={tgLink}
              copied={copiedKey === "tg"}
              onCopy={() => copy(tgLink, "tg")}
              testid={`aff-link-tg-${code.code}`}
            />
            <LinkRow
              label={t("codes.link.web")}
              url={webLink}
              copied={copiedKey === "web"}
              onCopy={() => copy(webLink, "web")}
              testid={`aff-link-web-${code.code}`}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function LinkRow({ label, url, copied, onCopy, testid }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground w-20">
        {label}
      </span>
      <code className="flex-1 bg-background border border-border px-2 py-1 font-mono break-all">
        {url}
      </code>
      <button
        onClick={onCopy}
        data-testid={testid}
        className="text-muted-foreground hover:text-foreground p-1.5 border border-border"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function CreateCodeModal({ onClose, onCreated, t }) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/affiliate/codes", {
        code: code.toUpperCase(),
        label: label || undefined,
      });
      toast.success(t("codes.created"));
      onCreated();
    } catch (er) {
      toast.error(formatApiError(er));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="aff-create-code-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-background border border-border max-w-md w-full p-5 space-y-3"
      >
        <h2 className="text-lg font-medium">{t("codes.modal.title")}</h2>
        <div>
          <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground block mb-1">
            {t("codes.modal.code_label")}
          </label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            pattern="[A-Z0-9_-]{3,32}"
            placeholder={t("codes.modal.code_placeholder")}
            data-testid="aff-new-code-value"
            className="w-full px-2 py-1.5 bg-background border border-border text-sm font-mono focus:outline-none focus:border-foreground/40"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground block mb-1">
            {t("codes.modal.label_label")}
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("codes.modal.label_placeholder")}
            data-testid="aff-new-code-label"
            className="w-full px-2 py-1.5 bg-background border border-border text-sm focus:outline-none focus:border-foreground/40"
          />
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-border">
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="aff-new-code-submit"
            className="px-3 py-1.5 text-sm bg-foreground text-background disabled:opacity-50"
          >
            {busy ? t("codes.modal.busy") : t("codes.modal.cta")}
          </button>
        </div>
      </form>
    </div>
  );
}
